import React from "react";
import { Col } from "react-bootstrap";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Label } from "recharts";
import { LineChart, Line } from "recharts";
import { LineChart as LucideLineChart, BarChart3, PieChart as PieIcon } from "lucide-react";
import { motion } from "framer-motion";

const MarketProjections = ({ dataProjections, dataHistory }) => {
    // Parse projections for charts
    const chartData = dataHistory.map(item => ({
        year: item.year,
        value: parseFloat(item.value.replace(/[^\d.-]/g, ''))
    }));

    return (
        <div className="mt-12">
            <div className="d-flex align-items-center gap-2 mb-6">
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                    <LucideLineChart size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-800 m-0">Market Growth & Projections (2020-2030)</h3>
            </div>

            <div className="row g-4">
                <Col lg={8}>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 h-100">
                        <h4 className="text-sm font-bold text-gray-500 mb-4 uppercase tracking-wider">Total Utah Construction Output ($ Billion)</h4>
                        <div style={{ height: 350 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 30, bottom: 40 }}>
                                    <defs>
                                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1} />
                                            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} interval={0}>
                                        <Label value="Calendar Year" offset={-20} position="insideBottom" style={{ fontSize: '11px', fill: '#9ca3af', fontWeight: 600 }} />
                                    </XAxis>
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }}>
                                        <Label value="Output ($ Billion)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: '11px', fill: '#9ca3af', fontWeight: 600 }} offset={0} />
                                    </YAxis>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                        formatter={(val) => [`$${val} Billion`, "Market Value"]}
                                    />
                                    <Area type="monotone" dataKey="value" stroke="#4F46E5" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        <p className="mt-4 text-xs text-gray-400 italic">
                            * Total Construction Output refers to spending on residential, non-residential, and infrastructure.
                        </p>
                    </div>
                </Col>

                <Col lg={4}>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 h-100">
                        <h4 className="text-sm font-bold text-gray-500 mb-6 uppercase tracking-wider">Sector CAGR Projections</h4>
                        <div className="space-y-6">
                            {dataProjections.slice(0, 4).map((item, i) => (
                                <div key={i} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="text-xs font-bold text-gray-700">{item.segment}</span>
                                        <span className="text-xs font-bold text-green-600">{(item.cagr * 100).toFixed(1)}% CAGR</span>
                                    </div>
                                    <div className="progress" style={{ height: '6px', borderRadius: '10px' }}>
                                        <div
                                            className="progress-bar bg-indigo-600"
                                            style={{ width: `${(parseFloat(item.value_2030.replace(/[^\d.-]/g, '')) / 34.5) * 100}%` }}
                                        ></div>
                                    </div>
                                    <div className="d-flex justify-content-between mt-1">
                                        <span className="text-[10px] text-gray-400">{item.value_2026} (2026)</span>
                                        <span className="text-[10px] text-gray-500 font-bold">{item.value_2030} (2030)</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </div>
        </div>
    );
};

export default MarketProjections;
